import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/LandingPage/PhasesHooks/ResearchFoundations.css';

const researchAreas = [
  {
    icon: 'ti-calculator',
    title: 'IFRS 17 & Capital Frameworks',
    desc: 'Deep-dive studies into insurance contract accounting, measurement models, and the interplay between regulatory capital and financial reporting.',
    status: 'Active',
    papers: 3,
  },
  {
    icon: 'ti-shield-lock',
    title: 'Solvency & Stress Testing',
    desc: 'Research on solvency assessment methodologies, ORSA frameworks, and dynamic stress testing protocols for insurance balance sheets.',
    status: 'Active',
    papers: 2,
  },
  {
    icon: 'ti-heartbeat',
    title: 'Mortality & Morbidity',
    desc: 'Experience analyses, longevity trend modelling, and pandemic impact studies on life and health insurance portfolios.',
    status: 'Active',
    papers: 4,
  },
  {
    icon: 'ti-chart-arrows',
    title: 'Asset-Liability Modelling',
    desc: 'ALM technique development, interest rate risk modelling, and strategic asset allocation under multi-scenario frameworks.',
    status: 'Active',
    papers: 2,
  },
  {
    icon: 'ti-cloud-storm',
    title: 'Climate & Parametric Insurance',
    desc: 'Climate-linked risk transfer mechanisms, parametric trigger design, and nature-based insurance solution frameworks.',
    status: 'Active',
    papers: 1,
  },
  {
    icon: 'ti-brain',
    title: 'AI in Actuarial Modelling',
    desc: 'Machine learning applications in reserving, pricing automation, and predictive analytics for actuarial decision-making.',
    status: 'Active',
    papers: 2,
  },
];

const milestones = [
  { quarter: 'Q1 2026', milestone: 'IFRS 17 Technical Framework Published' },
  { quarter: 'Q2 2026', milestone: 'Solvency Stress Testing Whitepaper' },
  { quarter: 'Q3 2026', milestone: 'Mortality Experience Analysis Release' },
  { quarter: 'Q4 2026', milestone: 'Climate Risk Transfer Framework' },
];

const researchPaperLinks = [
  { label: 'Whitepapers', path: '/research-papers/whitepapers', icon: 'ti-file-text' },
  { label: 'Actuarial Insight Briefs', path: '/research-papers/actuarial-insight-briefs', icon: 'ti-file-analytics' },
  { label: 'Industry Commentary Reports', path: '/research-papers/industry-commentary', icon: 'ti-file-report' },
  { label: 'Conference Papers', path: '/research-papers/conference-papers', icon: 'ti-presentation' },
  { label: 'Technical Notes', path: '/research-papers/technical-notes', icon: 'ti-notes' },
  { label: 'Public Dashboards', path: '/research-papers/public-dashboards', icon: 'ti-layout-dashboard' },
  { label: 'Regulatory Response Submissions', path: '/research-papers/regulatory-responses', icon: 'ti-file-check' },
];

export default function ResearchFoundations({ onClose }) {
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const navigate = useNavigate();
  const [showPaperPicker, setShowPaperPicker] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (alertVisible) {
          setAlertVisible(false);
        } else if (showPaperPicker) {
          setShowPaperPicker(false);
        } else {
          onClose();
        }
      }
    };
    const handleClickOutside = (e) => {
      if (modalRef.current && !contentRef.current.contains(e.target)) {
        if (alertVisible) {
          setAlertVisible(false);
        } else if (showPaperPicker) {
          setShowPaperPicker(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    modalRef.current?.addEventListener('click', handleClickOutside);
    requestAnimationFrame(() => {
      modalRef.current?.classList.add('is-open');
      contentRef.current?.classList.add('is-visible');
    });
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, showPaperPicker, alertVisible]);

  const handleClose = () => {
    modalRef.current?.classList.remove('is-open');
    contentRef.current?.classList.remove('is-visible');
    setTimeout(onClose, 400);
  };

  const handleViewPapers = () => {
    setShowPaperPicker(true);
  };

  const handlePaperSelect = (path) => {
    setPendingPath(path);
    setShowPaperPicker(false);
    setAlertVisible(true);
  };

  const handleAlertConfirm = () => {
    setAlertVisible(false);
    handleClose();
    setTimeout(() => {
      if (pendingPath) navigate(pendingPath);
    }, 500);
  };

  return (
    <div className="phase-modal-overlay" ref={modalRef}>
      <div className="phase-modal" ref={contentRef}>

        <div className="modal-ambient">
          <div className="modal-orb orb-teal" />
          <div className="modal-orb orb-green" />
          <div className="modal-grid" />
        </div>

        <button className="modal-close" onClick={handleClose} aria-label="Close modal">
          <i className="ti ti-x" />
        </button>

        <div className="modal-header">
          <div className="modal-badge">
            <span className="badge-ping" />
            Phase 1 — Active
          </div>
          <h2>Research Foundations</h2>
          <p className="modal-subtitle">
            Independent actuarial research establishing the intellectual backbone
            of every future advisory engagement.
          </p>
        </div>

        <div className="research-grid">
          {researchAreas.map((area, i) => (
            <div className="research-card" key={area.title} style={{ '--ri': i }}>
              <div className="research-card-glow" />
              <div className="research-icon-wrap">
                <i className={`ti ${area.icon}`} />
              </div>
              <h4>{area.title}</h4>
              <p>{area.desc}</p>
              <div className="research-meta">
                <span className="meta-status">
                  <span className="status-dot" />
                  {area.status}
                </span>
                <span className="meta-papers">{area.papers} papers</span>
              </div>
            </div>
          ))}
        </div>

        <div className="modal-timeline">
          <h3>Research Roadmap</h3>
          <div className="timeline-track">
            {milestones.map((m, i) => (
              <div className="timeline-node" key={m.quarter} style={{ '--ti': i }}>
                <div className="timeline-dot">
                  <div className="timeline-ring" />
                </div>
                <span className="timeline-quarter">{m.quarter}</span>
                <span className="timeline-label">{m.milestone}</span>
              </div>
            ))}
            <div className="timeline-line" />
          </div>
        </div>

        <div className="modal-footer">
          <p>All research outputs are peer-reviewed before publication.</p>
          <button className="modal-cta" onClick={handleViewPapers}>
            <span>View Research Papers</span>
            <i className="ti ti-arrow-right" />
          </button>
        </div>

        {/* ── Paper Picker Popup ── */}
        {showPaperPicker && (
          <div className="picker-overlay" onClick={() => setShowPaperPicker(false)}>
            <div className="picker-panel" onClick={(e) => e.stopPropagation()}>
              <div className="picker-header">
                <h4>Select Research Category</h4>
                <button className="picker-close" onClick={() => setShowPaperPicker(false)}>
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="picker-list">
                {researchPaperLinks.map((paper, i) => (
                  <button
                    key={paper.path}
                    className="picker-item"
                    style={{ '--pi': i }}
                    onClick={() => handlePaperSelect(paper.path)}
                  >
                    <span className="picker-icon"><i className={`ti ${paper.icon}`} /></span>
                    <span className="picker-label">{paper.label}</span>
                    <i className="ti ti-chevron-right picker-chevron" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Alert Notification ── */}
        {alertVisible && (
          <div className="alert-overlay" onClick={() => setAlertVisible(false)}>
            <div className="alert-box" onClick={(e) => e.stopPropagation()}>
              <div className="alert-icon">
                <i className="ti ti-bulb" />
              </div>
              <h4>Did you know?</h4>
              <p>
                You can also access all research papers anytime from the
                <strong> Research Papers</strong> dropdown in the navigation bar.
              </p>
              <button className="alert-cta" onClick={handleAlertConfirm}>
                Continue to Paper
                <i className="ti ti-arrow-right" />
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
